interface SabreLogoProps {
  /** Rendered width in pixels; the wordmark keeps its aspect ratio. */
  width?: number;
  className?: string;
  title?: string;
}

/** Natural size of the artwork below; every render scales from this box. */
const VIEW_WIDTH = 369;
const VIEW_HEIGHT = 78;

/**
 * The "sabre" letterforms, traced from the supplied wordmark. Every edge is
 * axis-aligned or a true 45° chamfer, which is what gives the mark its cut
 * geometry — so it is drawn as outlines rather than set in a heavy sans, which
 * only ever approximated it and shifted with whatever font the platform had.
 *
 * Counters wind against their outer contour, so the default nonzero fill rule
 * punches them out correctly.
 */
const WORDMARK =
  'M12 20 L63 20 L63 30 L11 30 L11 44 L63 44 L63 65 L50 78 L0 78 L0 68 L52 68 L52 54 L0 54 L0 32Z' +
  'M73 20 L123 20 L135 32 L135 78 L85 78 L73 66 L73 44 L125 44 L125 31 L124 30 L73 30Z' +
  'M84 54 L84 68 L124 68 L125 67 L125 54Z' +
  'M146 0 L156 0 L156 20 L208 20 L208 66 L196 78 L146 78Z' +
  'M157 30 L156 31 L156 67 L157 68 L197 68 L197 30Z' +
  'M219 20 L281 20 L281 30 L229 30 L229 78 L219 78Z' +
  'M291 20 L342 20 L343 23 L345 25 L347 25 L350 28 L350 30 L354 33 L354 54 L302 54 L302 68 L354 68 L354 77 L353 78 L304 78 L291 65Z' +
  'M302 30 L302 44 L343 44 L343 30Z';

/**
 * The registered mark. In the source artwork it is an 11px hairline ring, too
 * small to trace into anything but noise, so it is drawn as exact geometry: a
 * ring (outer arc clockwise, inner counter-clockwise) and an R. It reads as a
 * dot at footer size and stays crisp when the mark is rendered large.
 */
const REGISTERED =
  'M358 72.5 A5.5 5.5 0 1 1 369 72.5 A5.5 5.5 0 1 1 358 72.5Z' +
  'M359.2 72.5 A4.3 4.3 0 1 0 367.8 72.5 A4.3 4.3 0 1 0 359.2 72.5Z' +
  'M361.6 69.7 L364.2 69.7 L365.3 70.8 L365.3 71.6 L364.5 72.4 L365.5 75.3 L364.2 75.3 L363.3 72.6 L362.7 72.6 L362.7 75.3 L361.6 75.3Z' +
  'M362.7 70.8 L362.7 71.6 L364.2 71.6 L364.2 70.8Z';

export function SabreLogo({ width = 240, className, title = 'Sabre' }: SabreLogoProps) {
  const height = Math.round((width * VIEW_HEIGHT) / VIEW_WIDTH);
  return (
    <svg
      className={className}
      width={width}
      height={height}
      viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
      role="img"
      aria-label={title}
      fill="currentColor"
    >
      <path d={WORDMARK} />
      <path d={REGISTERED} />
    </svg>
  );
}
